import { Link } from 'react-router-dom';
import { Grid, Typography, Breadcrumbs, Box, IconButton, Tooltip, useMediaQuery, useTheme, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import PageTitleWrapper from 'src/components/molecules/pageHeader/PageTitleWrapper';
import { useTranslation } from 'react-i18next';
import AdditionalActions from '../atoms/buttons/AdditionalActions';
import TooltipField from '../atoms/TooltipField';
import { customComponent } from 'src/interfaces/customComponent';
import { separateMenuComponents } from 'src/utils/buttons/separateMenuComponents';
import useBreadcrumbs from 'src/hooks/pageHeader/useBreadcrumbs';
import { CustomAlertProps } from '../atoms/CustomAlert';
import { useContext } from 'react';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useSettingsContext } from 'src/hooks/useSettings';
import FloatingChat from './chat/Chat';
import React from 'react';
import CustomButton from '../atoms/buttons/CustomButton';
import { FormInspectionButton } from '../molecules/formik/FormInspectionButton';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TESTING_FUNCTIONALITY } from 'src/utils/enviromentValues';
import { useVideoDialog, VideoButton, VideoDialog, VideoDialogInput } from '../molecules/video-dialog/VideoDialog';

export interface PageHeaderProps {
  /**
   * Backward compatible:
   *  - string (existing usage)
   *  - React.ReactNode (existing usage)
   *  - { text: string; after?: React.ReactNode } (new: allows trailing component after the title text)
   */
  title: string | React.ReactNode | { text: string; after?: React.ReactNode };
  subTitle?: string | React.ReactNode;
  tooltipText?: string;
  imageSrc?: string;
  components?: customComponent[];
  video?: VideoDialogInput;
  alertData?: CustomAlertProps;
  initialValues?: any;
}

const PageHeader = (pageHeaderData: PageHeaderProps) => {
  const { t }: { t: any } = useTranslation();
  const breadcrumbs = useBreadcrumbs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const videoRef = useVideoDialog(pageHeaderData.video);
  const components = pageHeaderData.video
    ? [{ component: <VideoButton videoRef={videoRef} /> }, ...(pageHeaderData.components ?? [])]
    : pageHeaderData.components;

  const [TopRightComponents, MenuItemsComponents] = isMobile
    ? [[], components ?? []]
    : separateMenuComponents(components);
  const { settings } = useSettingsContext();

  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);

  // Normalize title into { text, after?, rawNode? } so Helmet always gets a plain string
  const normalizeTitle = (
    input: PageHeaderProps['title']
  ): { text: string; after?: React.ReactNode; rawNode?: React.ReactNode } => {
    // New compound format
    if (input && typeof input === 'object' && !React.isValidElement(input) && 'text' in (input as any)) {
      const { text, after } = input as { text: string; after?: React.ReactNode };
      return { text, after };
    }
    // Plain string (legacy)
    if (typeof input === 'string') {
      return { text: input };
    }
    // React node (legacy) – render as-is for the visual title, but Helmet needs a string.
    // Fallback to empty Helmet title (or you can supply a default).
    return { text: '', rawNode: input as React.ReactNode };
  };

  const { text: titleText, after: titleAfter, rawNode: titleRawNode } = normalizeTitle(pageHeaderData.title);

  return (
    <>
      <Helmet>
        <title>{titleText || ''}</title>
      </Helmet>
      <PageTitleWrapper>
        <Grid
          container
          columnSpacing={2}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          {/* Left cluster: title + subtitle/breadcrumbs + image */}
          <Grid item sx={{ maxWidth: { xs: '100%', md: '65%' }, width: { xs: '101%', md: 'initial' } }}>
            <Box display="flex" flexDirection="row" alignItems="flex-start">
              {/* Text block */}
              <Box sx={{ maxWidth: { xs: '100%', md: 'calc(100% - 0px)' }, width: '100%' }}>
                {/* Row 1: title + mobile menu button */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    width: '100%',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant={isMobile ? 'h4' : 'h3'}
                    component="h3"
                    gutterBottom
                    sx={isMobile ? { fontSize: '1.3rem', flexGrow: 1, minWidth: 0 } : { flexGrow: 1, minWidth: 0 }}
                    noWrap={false}
                  >
                    {titleText ? t(titleText) : titleRawNode}
                    {/* Append trailing component if provided (e.g., copy button) */}
                    {titleAfter ?? null}
                    <TooltipField tooltipText={pageHeaderData.tooltipText} />
                    {isMobile && pageHeaderData.subTitle && (
                      <Tooltip title={pageHeaderData.subTitle} arrow>
                        <IconButton
                          size="small"
                          sx={{
                            ml: 1,
                            mt: '2px',
                            p: 0.5
                          }}
                        >
                          <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Typography>

                  {/* MOBILE MENU TOGGLE */}
                  <Box
                    sx={{
                      display: { xs: 'block', md: 'none' },
                      ml: 1,
                      flexShrink: 0
                    }}
                  >
                    <Tooltip arrow title="Toggle Menu">
                      <IconButton color="primary" onClick={toggleSidebar}>
                        {!sidebarToggle ? <MenuTwoToneIcon /> : <CloseTwoToneIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {!isMobile && (
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, maxWidth: { xs: '100%', md: 600 }, whiteSpace: 'normal' }}
                  >
                    {pageHeaderData.subTitle}
                  </Typography>
                )}

                {/* Row 2: breadcrumbs + mobile MenuItemsComponents */}
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 1
                  }}
                >
                  <Breadcrumbs
                    maxItems={5}
                    aria-label="breadcrumb"
                    sx={{ mb: 0, flexGrow: 1, minWidth: 0 }}
                  >
                    {breadcrumbs.path.map((path, i) => (
                      <Link key={'link' + i} color="inherit" to={path}>
                        {t(breadcrumbs.label[i])}
                      </Link>
                    ))}
                  </Breadcrumbs>

                  {isMobile && (
                    <Box sx={{ ml: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                      <AdditionalActions>
                        {MenuItemsComponents?.map((customComponent, i) => (
                          <Grid key={i} item xs={12}>
                            {modifyComponentVariant(customComponent.component)}
                          </Grid>
                        ))}
                        {pageHeaderData?.initialValues && TESTING_FUNCTIONALITY && (
                          <Grid item xs={12}>
                            <FormInspectionButton initialValues={pageHeaderData?.initialValues} />
                          </Grid>
                        )}
                      </AdditionalActions>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Image snug next to text */}
              {pageHeaderData.imageSrc && (
                <Box
                  component="img"
                  src={pageHeaderData.imageSrc}
                  alt="Header image"
                  sx={{
                    maxHeight: 70,
                    width: 'auto',
                    objectFit: 'contain',
                    ml: 2,
                    mt: 0.5,
                  }}
                />
              )}
            </Box>
          </Grid>

          {/* Right-side actions (desktop only) */}
          <Grid
            item
            xs="auto"
            sm="auto"
            sx={{ mt: { xs: 0, md: 4 }, display: { xs: 'none', md: 'block' } }}
          >
            <Box
              display="flex"
              justifyContent={{ xs: 'flex-end', sm: 'flex-end' }}
              flexWrap="wrap"
              width="100%"
            >
              {TopRightComponents?.map((customComponent, i) => (
                <Box key={i} pl={1}>
                  {customComponent.component}
                </Box>
              ))}
              {!isMobile && (
                <AdditionalActions>
                  {MenuItemsComponents?.map((customComponent, i) => (
                    <Grid key={i} item xs={12}>
                      {modifyComponentVariant(customComponent.component)}
                    </Grid>
                  ))}
                  {pageHeaderData?.initialValues && TESTING_FUNCTIONALITY && (
                    <Grid item xs={12}>
                      <FormInspectionButton initialValues={pageHeaderData?.initialValues} />
                    </Grid>
                  )}
                </AdditionalActions>
              )}
            </Box>
          </Grid>
        </Grid>
      </PageTitleWrapper>

      {pageHeaderData.video && <VideoDialog videoRef={videoRef} />}

      {/* {settings?.ShowChat ? <FloatingChat /> : null} */}
    </>
  );
}

export default PageHeader;

const hasVariantProp = (component: React.ReactElement): component is React.ReactElement<{ variant?: string }> => {
  return component.props && ('variant' in component.props || isButtonLikeComponent(component));
};

const isButtonLikeComponent = (component: React.ReactElement) => {
  const componentType = component.type;

  if (typeof componentType !== 'string' && componentType) {
    return componentType === Button || componentType === IconButton || componentType === CustomButton;
  } else {
    //  console.log(componentType);
  }

  return false;
};

const modifyComponentVariant = (component: React.ReactNode) => {
  if (React.isValidElement(component) && hasVariantProp(component)) {
    return React.cloneElement(component, { variant: 'text' });
  }
  return component;
};